import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { TokenLogo } from "./common";
import { shortAddr } from "../lib/format";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [openDD, setOpenDD] = useState(false);
  const nav = useNavigate();
  const boxRef = useRef();

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpenDD(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (!boxRef.current?.contains(e.target)) setOpenDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpenDD(false); setQ(""); nav(path); };

  const pathFor = (item) => {
    const t = item.type;
    if (t === "token") return `/token/${item.address_hash || item.address}`;
    if (t === "transaction") return `/explorer/tx/${item.transaction_hash || item.tx_hash || item.hash}`;
    if (t === "block") return `/explorer/block/${item.block_number ?? item.block_hash}`;
    return `/wallet/${item.address_hash || item.address}`;
  };

  const submit = (e) => {
    e.preventDefault();
    const s = q.trim();
    if (!s) return;
    if (/^0x[a-fA-F0-9]{64}$/.test(s)) return go(`/explorer/tx/${s}`);
    if (/^0x[a-fA-F0-9]{40}$/.test(s)) return go(`/wallet/${s}`);
    if (/^\d+$/.test(s)) return go(`/explorer/block/${s}`);
    if (res?.items?.[0]) return go(pathFor(res.items[0]));
  };

  return (
    <div ref={boxRef} className="relative">
      <form onSubmit={submit}>
        <div className="flex items-center gap-2 bg-[#121212] border border-[#262626] rounded-sm px-2.5 h-8 focus-within:border-[#059669] transition-colors">
          <Search size={14} className="text-[#6B7280] shrink-0" />
          <input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => res && setOpenDD(true)}
            placeholder="Search token, wallet, tx, block…"
            className="bg-transparent outline-none text-[13px] w-full placeholder:text-[#4B5563]"
          />
        </div>
      </form>
      {openDD && res && (
        <div className="absolute top-9 inset-x-0 bg-[#121212] border border-[#262626] rounded-sm z-50 max-h-96 overflow-y-auto" data-testid="search-results">
          {res.items?.map((item, i) => (
            <button key={i} className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#1A1A1A] text-left" onClick={() => go(pathFor(item))} data-testid={`search-result-${i}`}>
              {item.type === "token" ? (
                <>
                  <TokenLogo symbol={item.symbol} icon={item.icon_url} size={20} />
                  <span className="text-[13px]">{item.name}</span>
                  <span className="num text-xs text-[#6B7280]">{item.symbol}</span>
                  <span className="ml-auto num text-[11px] text-[#6B7280]">{shortAddr(item.address_hash || item.address || "")}</span>
                </>
              ) : (
                <>
                  <span className="text-[10px] uppercase text-[#6B7280] w-16">{item.type}</span>
                  <span className="num text-[13px]">{item.name || shortAddr(item.address_hash || item.transaction_hash || item.block_hash || "", 8)}</span>
                  {item.block_number != null && <span className="num text-xs text-[#6B7280]">#{item.block_number}</span>}
                </>
              )}
            </button>
          ))}
          {!res.items?.length && <div className="px-3 py-3 text-[13px] text-[#6B7280]">No results on Robinhood Chain</div>}
        </div>
      )}
    </div>
  );
}
